import { cva } from "cva";
import { BuildHtml } from "~/components/BuildHtml";
import {
  LandingPageHeaderAlignment,
  LandingPageHeaderOrientation,
  LandingPageHeaderTitleLevel,
} from "~/landing-pages/landing-page-types";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

const innerContainerStyles = cva("max-w-2xl", {
  defaultVariants: {
    textLocation: "start",
  },
  variants: {
    textLocation: {
      center: "mx-auto",
      end: "ml-auto",
      start: "",
    },
    textAlignment: {
      center: "text-center",
      end: "text-right",
      start: "text-left",
    },
  },
});

const eyebrowStyles = cva("font-semibold", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-brand-indigo",
      dark: "text-brand-indigo-200",
      brand: "text-brand-indigo-100",
      dark_image: "text-brand-indigo-200",
      light_image: "text-brand-indigo",
    },
  },
});

const titleStyles = cva("mt-2", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-gray-900",
      dark: "text-white",
      brand: "text-white",
      dark_image: "text-white",
      light_image: "text-gray-900",
    },
  },
});

const descriptionStyles = cva("mt-6", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-gray-900",
      dark: "text-gray-200",
      brand: "text-gray-200",
      dark_image: "text-gray-200",
      light_image: "text-gray-900",
    },
  },
});

export type HeaderBaseProps = {
  appearance: "brand" | "dark" | "light" | "dark_image" | "light_image";
  description: string;
  eyebrow: string;
  textLocation?: LandingPageHeaderOrientation;
  textAlignment?: LandingPageHeaderAlignment;
  title: string;
  titleLevel: LandingPageHeaderTitleLevel;
};

/** This is the display component for the header text.
 * This also controls the themed background.
 *
 * This should not be treated as a public-facing component, as
 * the types are filtered down
 */
export const HeaderBase: React.FC<HeaderBaseProps> = ({
  appearance,
  description,
  eyebrow,
  textLocation,
  textAlignment,
  title,
  titleLevel,
}) => (
  <div
    className={innerContainerStyles({
      textLocation,
      textAlignment,
    })}
  >
    <Headline
      level={5}
      className={eyebrowStyles({
        appearance,
      })}
    >
      {eyebrow}
    </Headline>
    <Headline
      as={titleLevel}
      level={0}
      className={titleStyles({
        appearance,
      })}
    >
      {title}
    </Headline>
    <Body size="large" className={descriptionStyles({ appearance })}>
      <BuildHtml html={description} tagToClassNameMap={tagToClassNameMap} />
    </Body>
  </div>
);
